/**
 * Main 3D scene component
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Grid } from "@react-three/drei";
import { ActionIcon, Box, Button, Text } from "@mantine/core";
import { IconPlugConnected, IconRotate3d } from "@tabler/icons-react";
import * as THREE from "three";
import {
  useMeshes,
  useCamera,
  useBackgroundColor,
  useViewerStore,
  useIKEnabled,
  useIKGizmos,
  useHiddenGizmoIds,
  useHighlightedMeshUuids,
  useCollidingMeshUuids,
  useEndCoordsGizmoVisible,
  useInstances,
  useJoints,
  useSelectedModuleInstanceId,
  usePendingPlacementModule,
  useJointAxesMode,
} from "../store";
import { useWebSocket } from "../WebSocket";
import { recordDisconnect, recordDelete, recordJointBatch } from "../hooks";
import { SceneMesh } from "./SceneMesh";
import { IKGizmo } from "./IKGizmo";
import { EndCoordsGizmo } from "./EndCoordsGizmo";
import { JointAxisVisualization } from "./JointAxisVisualization";
import { JointAxesOverlay } from "./JointAxesOverlay";
import { ConnectionPointMarkers } from "./ConnectionPointMarkers";
import { ConnectSnapEffect } from "./ConnectSnapEffect";
import { DimensionAnnotation } from "./DimensionAnnotation";
import { isPlacementTap, type PointerSample } from "./placementTap";
import { DragHandler } from "./DragHandler";
import { ContextMenuItem, type ContextMenuState } from "./SceneContextMenu";

// Store scene utilities for external access
interface SceneUtils {
  getGroundPosition: (screenX: number, screenY: number) => [number, number, number] | null;
  // The live three.js scene, so code outside the Canvas (the AR export) can
  // read what is actually on screen rather than rebuild it from the store.
  getScene: () => THREE.Scene;
}

let sceneUtils: SceneUtils | null = null;

export function getSceneUtils(): SceneUtils | null {
  return sceneUtils;
}

// Reserved name for the end coords a long-press creates. Must match
// SCRATCH_END_COORDS_PREFIX in robot_compiler/viewer/config_io.py, which drops
// these when a config is saved.
const SCRATCH_IK_END_COORDS_NAME = "__scratch_ik__target";

function CameraController() {
  const camera = useCamera();
  const { camera: threeCamera } = useThree();

  useEffect(() => {
    threeCamera.position.set(...camera.position);
    threeCamera.lookAt(new THREE.Vector3(...camera.target));
  }, [camera, threeCamera]);

  return null;
}

function SceneUtilsProvider() {
  const { camera, gl, scene } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  // Z-up coordinate system: ground plane is XY at Z=0
  const groundPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0));

  const getGroundPosition = useCallback(
    (screenX: number, screenY: number): [number, number, number] | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const x = ((screenX - rect.left) / rect.width) * 2 - 1;
      const y = -((screenY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(new THREE.Vector2(x, y), camera);

      const intersection = new THREE.Vector3();
      const result = raycaster.current.ray.intersectPlane(groundPlane.current, intersection);

      if (result) {
        return [intersection.x, intersection.y, intersection.z];
      }
      return null;
    },
    [camera, gl]
  );

  useEffect(() => {
    sceneUtils = { getGroundPosition, getScene: () => scene };
    return () => {
      sceneUtils = null;
    };
  }, [getGroundPosition, scene]);

  return null;
}

function SceneContent({ isDragging, onIKDragStateChange, onEndCoordsDragStateChange, scratchIkActive }: { isDragging: boolean; onIKDragStateChange: (isDragging: boolean) => void; onEndCoordsDragStateChange: (isDragging: boolean) => void; scratchIkActive: boolean }) {
  const meshes = useMeshes();
  const ikEnabled = useIKEnabled();
  const ikGizmos = useIKGizmos();
  const hiddenGizmoIds = useHiddenGizmoIds();
  const highlightedMeshUuids = useHighlightedMeshUuids();
  const collidingMeshUuids = useCollidingMeshUuids();
  const endCoordsGizmoVisible = useEndCoordsGizmoVisible();
  const joints = useJoints();
  // Long-pressing a link asks for that link, not for every end-effector in the
  // assembly. While a scratch target is up, it is the only gizmo shown; the
  // server names a custom end coords' gizmo after the end coords itself.
  const visibleGizmos = ikGizmos
    .filter((g) => !hiddenGizmoIds[g.gizmo_id])
    .filter((g) => !scratchIkActive || g.link_name === SCRATCH_IK_END_COORDS_NAME);

  // Capture all joint angles at IK drag start for undo
  const handleIKDragStart = useCallback(() => {
    recordJointBatch(joints.map((j) => ({ name: j.name, value: j.current_angle })));
    onIKDragStateChange(true);
  }, [joints, onIKDragStateChange]);
  const handleIKDragEnd = useCallback(() => onIKDragStateChange(false), [onIKDragStateChange]);
  const handleEndCoordsDragStart = useCallback(() => onEndCoordsDragStateChange(true), [onEndCoordsDragStateChange]);
  const handleEndCoordsDragEnd = useCallback(() => onEndCoordsDragStateChange(false), [onEndCoordsDragStateChange]);

  // Create a Set for faster lookup
  const highlightedSet = new Set(highlightedMeshUuids);
  const collidingSet = new Set(collidingMeshUuids);

  return (
    <>
      <CameraController />
      <SceneUtilsProvider />

      {/* Lighting */}
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
      <directionalLight position={[-10, -10, -5]} intensity={0.3} />

      {/* Grid on XY plane (Z-up coordinate system) - raycast disabled */}
      <group raycast={() => null}>
        <Grid
          args={[10, 10]}
          cellSize={0.1}
          cellThickness={0.5}
          cellColor="#6e6e6e"
          sectionSize={1}
          sectionThickness={1}
          sectionColor="#9d4b4b"
          fadeDistance={30}
          fadeStrength={1}
          followCamera={false}
          infiniteGrid
          rotation={[Math.PI / 2, 0, 0]}
        />
      </group>

      {/* Render all meshes */}
      {Object.values(meshes).map((mesh) => (
        <SceneMesh
          key={mesh.uuid}
          mesh={mesh}
          isHighlighted={highlightedSet.has(mesh.uuid)}
          isColliding={collidingSet.has(mesh.uuid)}
        />
      ))}

      {/* Connection Point Markers */}
      <ConnectionPointMarkers />
      <ConnectSnapEffect />
      <DimensionAnnotation />

      {/* IK Gizmos */}
      {ikEnabled && visibleGizmos.map((gizmo) => (
        <IKGizmo
          key={gizmo.gizmo_id}
          gizmo={gizmo}
          onDragStart={handleIKDragStart}
          onDragEnd={handleIKDragEnd}
        />
      ))}

      {/* End Coords Gizmo */}
      {endCoordsGizmoVisible && (
        <EndCoordsGizmo
          onDragStart={handleEndCoordsDragStart}
          onDragEnd={handleEndCoordsDragEnd}
        />
      )}

      {/* Joint Axis Visualization */}
      <JointAxisVisualization />

      {/* Every joint's axis at once, for checking how the servos are mounted */}
      <JointAxesOverlay />

      {/* Controls with Z-up - disabled during drag */}
      <OrbitControls makeDefault enabled={!isDragging} />
    </>
  );
}

export function Scene() {
  const connectModeActive = useViewerStore((state) => state.connectModeActive);
  const setConnectMode = useViewerStore((state) => state.setConnectMode);
  const jointAxesMode = useJointAxesMode();
  const setJointAxesMode = useViewerStore((state) => state.setJointAxesMode);
  const backgroundColor = useBackgroundColor();
  const [isDragging, setIsDragging] = useState(false);
  const [isIKDragging, setIsIKDragging] = useState(false);
  const [isEndCoordsDragging, setIsEndCoordsDragging] = useState(false);
  const endCoordsGizmoVisible = useEndCoordsGizmoVisible();
  const setSelectedMesh = useViewerStore((state) => state.setSelectedMesh);
  const ikEnabled = useIKEnabled();
  const setIKEnabled = useViewerStore((state) => state.setIKEnabled);
  const {
    sendSelectInstance,
    sendDeleteInstance,
    sendDisconnectInstance,
    sendAddModule,
    sendEnableIKMode,
    sendAddEndCoords,
    sendDeleteEndCoords,
    sendEnableJointAxes,
  } = useWebSocket();
  const [scratchIkLink, setScratchIkLink] = useState<string | null>(null);
  // IK mode as the user left it before a long press turned it on.
  const ikEnabledBeforeScratch = useRef<boolean | null>(null);
  const instances = useInstances();
  const selectedInstanceId = useSelectedModuleInstanceId();

  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // react-three-fiber measures this container once on mount to size the
  // renderer. When the measurement lands before the flex layout has settled
  // it reads zero and the canvas keeps its 300x150 default, leaving the
  // viewport blank until something else triggers a window resize. That is
  // easy to hit on the full page reload behind "Add to Scene", where the
  // session immediately loads an assembly. Watch the container and nudge
  // fiber to re-measure whenever the canvas disagrees with it.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return;
    }
    const syncCanvasToContainer = () => {
      const canvas = container.querySelector("canvas");
      if (!canvas) {
        return;
      }
      const { clientWidth: width, clientHeight: height } = container;
      if (width === 0 || height === 0) {
        return;
      }
      if (canvas.clientWidth !== width || canvas.clientHeight !== height) {
        window.dispatchEvent(new Event("resize"));
      }
    };
    const observer = new ResizeObserver(syncCanvasToContainer);
    observer.observe(container);
    syncCanvasToContainer();
    return () => observer.disconnect();
  }, []);

  const pendingPlacementModule = usePendingPlacementModule();
  const setPendingPlacementModule = useViewerStore((state) => state.setPendingPlacementModule);
  const tapStartRef = useRef<PointerSample | null>(null);

  const handlePlacementPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    tapStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      time: event.timeStamp,
      pointerId: event.pointerId,
    };
  }, []);

  const handlePlacementPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const start = tapStartRef.current;
      tapStartRef.current = null;
      if (!pendingPlacementModule || !start) {
        return;
      }
      const end: PointerSample = {
        x: event.clientX,
        y: event.clientY,
        time: event.timeStamp,
        pointerId: event.pointerId,
      };
      if (!isPlacementTap(start, end)) {
        // The camera was being orbited, not a spot being picked.
        return;
      }
      const groundPosition = getSceneUtils()?.getGroundPosition(event.clientX, event.clientY);
      if (!groundPosition) {
        return;
      }
      sendAddModule(pendingPlacementModule.id, groundPosition);
      setPendingPlacementModule(null);
    },
    [pendingPlacementModule, sendAddModule, setPendingPlacementModule]
  );

  // Combined dragging state
  const isAnyDragging = isDragging || isIKDragging || isEndCoordsDragging;

  // Deselect when clicking on empty space (not on any mesh)
  const handlePointerMissed = useCallback(() => {
    setSelectedMesh(null);
    sendSelectInstance(null);
  }, [setSelectedMesh, sendSelectInstance]);

  // Close context menu on any click outside the menu
  const contextMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClick = (e: PointerEvent) => {
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target as Node)) {
        return; // Click inside menu — don't close
      }
      setContextMenu(null);
    };

    document.addEventListener("pointerdown", handleClick);
    return () => {
      document.removeEventListener("pointerdown", handleClick);
    };
  }, []);

  // Long-press on a link: give IK a handle on exactly that link. The server
  // only ever builds gizmos for detected end-effectors and for end coords, so
  // a scratch end coords on the touched link is what turns it into a target.
  // Its reserved name keeps it out of saved configs (see config_io.py).
  useEffect(() => {
    const handleLongPress = (event: Event) => {
      const { linkName, instanceId } = (
        event as CustomEvent<{ linkName: string; instanceId?: string }>
      ).detail;
      if (!linkName) {
        return;
      }
      // Qualify the link with its instance: two copies of the same module have
      // identically named links, and the server resolves a bare name to
      // whichever module it happens to find first.
      const parentLinkName = instanceId ? `${instanceId}/${linkName}` : linkName;
      // Remember whether IK was the user's own choice, so releasing the link
      // does not leave the scene full of gizmos they never asked for.
      if (ikEnabledBeforeScratch.current === null) {
        ikEnabledBeforeScratch.current = useViewerStore.getState().ikEnabled;
      }
      setIKEnabled(true);
      sendEnableIKMode(true);
      // The server ignores an add for a name it already holds, so the previous
      // scratch target has to go first.
      sendDeleteEndCoords(SCRATCH_IK_END_COORDS_NAME);
      sendAddEndCoords(SCRATCH_IK_END_COORDS_NAME, parentLinkName, [0, 0, 0], [0, 0, 0]);
      setScratchIkLink(linkName);
    };

    window.addEventListener("mesh-long-press", handleLongPress);
    return () => window.removeEventListener("mesh-long-press", handleLongPress);
  }, [sendAddEndCoords, sendDeleteEndCoords, sendEnableIKMode, setIKEnabled]);

  const releaseScratchIkTarget = useCallback(() => {
    sendDeleteEndCoords(SCRATCH_IK_END_COORDS_NAME);
    setScratchIkLink(null);
    if (ikEnabledBeforeScratch.current === false) {
      setIKEnabled(false);
      sendEnableIKMode(false);
    }
    ikEnabledBeforeScratch.current = null;
  }, [sendDeleteEndCoords, sendEnableIKMode, setIKEnabled]);

  // Listen for right-click events from SceneMesh
  useEffect(() => {
    const handleMeshContextMenu = (e: Event) => {
      const { instanceId, clientX, clientY } = (e as CustomEvent).detail;
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setContextMenu({
          instanceId,
          x: clientX - rect.left,
          y: clientY - rect.top,
        });
      }
    };

    window.addEventListener("mesh-context-menu", handleMeshContextMenu);
    return () => {
      window.removeEventListener("mesh-context-menu", handleMeshContextMenu);
    };
  }, []);

  // Right-click on empty space shows scene context menu
  const handleNativeContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Only show scene menu if no module context menu is already open
    // and the click wasn't on a mesh (mesh right-clicks are handled separately)
    if (!contextMenu) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setContextMenu({
          instanceId: null,
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    }
  }, [contextMenu]);

  const handleDelete = useCallback((targetInstanceId?: string) => {
    const deleteId = targetInstanceId || contextMenu?.instanceId;
    if (!deleteId) return;
    const inst = instances.find((i) => i.instance_id === deleteId);
    if (inst) {
      recordDelete(inst, instances);
    }
    sendDeleteInstance(deleteId);
    setContextMenu(null);
  }, [contextMenu, instances, sendDeleteInstance]);

  const handleDisconnect = useCallback(() => {
    if (!contextMenu?.instanceId) return;
    const inst = instances.find((i) => i.instance_id === contextMenu.instanceId);
    if (inst && inst.parent_instance_id && inst.parent_link_name && inst.child_link_name) {
      recordDisconnect(inst.instance_id, inst.child_link_name, inst.parent_instance_id, inst.parent_link_name);
    }
    sendDisconnectInstance(contextMenu.instanceId);
    setContextMenu(null);
  }, [contextMenu, instances, sendDisconnectInstance]);

  const handleDuplicate = useCallback(() => {
    if (!contextMenu) return;
    const instance = instances.find((inst) => inst.instance_id === contextMenu.instanceId);
    if (!instance) return;

    // Find the module_id from the module_name (they are the same in this app)
    const pos = instance.position;
    const offset: [number, number, number] = [
      pos[0] + 0.1,
      pos[1] + 0.1,
      pos[2],
    ];
    sendAddModule(instance.module_name, offset);
    setContextMenu(null);
  }, [contextMenu, instances, sendAddModule]);

  // Check if the right-clicked instance has a parent (is connected)
  const contextInstance = contextMenu
    ? instances.find((inst) => inst.instance_id === contextMenu.instanceId)
    : null;
  const isConnected = contextInstance?.parent_instance_id != null;

  // For empty-space menu: check if there's a currently selected instance
  const selectedInstance = instances.find((inst) => inst.instance_id === selectedInstanceId);
  const isSelectedConnected = selectedInstance?.parent_instance_id != null;

  // Detect platform for shortcut display
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const modKey = isMac ? "\u2318" : "Ctrl+";

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", position: "relative" }}
      onContextMenu={handleNativeContextMenu}
      onPointerDown={handlePlacementPointerDown}
      onPointerUp={handlePlacementPointerUp}
    >
      <Canvas
        camera={{ position: [2, 2, 2], fov: 50, up: [0, 0, 1] }}
        style={{ width: "100%", height: "100%" }}
        gl={{ antialias: true }}
        onPointerMissed={handlePointerMissed}
      >
        <color attach="background" args={[backgroundColor]} />
        {/* While a module is armed the scene is in placement mode: taps pick a
            spot instead of grabbing whatever is under the finger. */}
        <DragHandler
          onDragStateChange={setIsDragging}
          disabled={endCoordsGizmoVisible || pendingPlacementModule !== null}
        >
          <SceneContent isDragging={isAnyDragging} onIKDragStateChange={setIsIKDragging} onEndCoordsDragStateChange={setIsEndCoordsDragging} scratchIkActive={scratchIkLink !== null} />
        </DragHandler>
      </Canvas>

      {/* Connect mode: shows the free connection points and makes a tap on
          one open the connect shelf. A mode rather than always-on markers,
          because permanently amber-studded robots read as noise. */}
      <ActionIcon
        variant={connectModeActive ? "filled" : "default"}
        color="orange"
        size={44}
        radius="xl"
        aria-label="Toggle connect mode"
        title={connectModeActive ? "接続モードを終了" : "接続点にモジュールを取り付ける"}
        onClick={() => setConnectMode(!connectModeActive)}
        style={{
          position: "absolute",
          right: 12,
          bottom: "calc(env(safe-area-inset-bottom) + 16px)",
          zIndex: 500,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}
      >
        <IconPlugConnected size={22} />
      </ActionIcon>

      {/* Axis overview: every joint's rotation axis in world coordinates,
          which is how a leg's servos get checked against each other. */}
      <ActionIcon
        variant={jointAxesMode ? "filled" : "default"}
        color="teal"
        size={44}
        radius="xl"
        aria-label="Toggle joint axis overview"
        title={jointAxesMode ? "回転軸の表示を終了" : "全関節の回転軸と向きを表示"}
        onClick={() => {
          const next = !jointAxesMode;
          setJointAxesMode(next);
          sendEnableJointAxes(next);
        }}
        style={{
          position: "absolute",
          right: 12,
          bottom: "calc(env(safe-area-inset-bottom) + 68px)",
          zIndex: 500,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}
      >
        <IconRotate3d size={22} />
      </ActionIcon>

      {scratchIkLink && !pendingPlacementModule && (
        <Box
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 12px",
            borderRadius: 8,
            backgroundColor: "rgba(64, 43, 122, 0.95)",
            color: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            maxWidth: "calc(100% - 24px)",
          }}
        >
          <Text size="sm" fw={500} style={{ wordBreak: "break-all" }}>
            Drag the gizmo to move {scratchIkLink}
          </Text>
          <Button size="compact-sm" variant="white" color="dark" onClick={releaseScratchIkTarget}>
            Done
          </Button>
        </Box>
      )}

      {pendingPlacementModule && (
        <Box
          // The banner sits inside the viewport, so without this a tap on
          // Cancel would bubble up as a placement tap and drop the module
          // under the button before the click handler ever ran.
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 12px",
            borderRadius: 8,
            backgroundColor: "rgba(34, 139, 230, 0.95)",
            color: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            maxWidth: "calc(100% - 24px)",
          }}
        >
          <Text size="sm" fw={500} style={{ wordBreak: "break-all" }}>
            Tap the ground to place {pendingPlacementModule.name}
          </Text>
          <Button
            size="compact-sm"
            variant="white"
            color="dark"
            onClick={() => setPendingPlacementModule(null)}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          style={{
            position: "absolute",
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000,
            background: "var(--mantine-color-body, #fff)",
            border: "1px solid var(--mantine-color-default-border, #dee2e6)",
            borderRadius: 4,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            padding: "4px 0",
            minWidth: 170,
          }}
        >
          {contextMenu.instanceId ? (
            <>
              {/* Module context menu */}
              {contextInstance && (
                <div style={{
                  padding: "4px 12px",
                  fontSize: 12,
                  color: "var(--mantine-color-dimmed, #868e96)",
                  borderBottom: "1px solid var(--mantine-color-default-border, #dee2e6)",
                  marginBottom: 2,
                }}>
                  {contextInstance.module_name}
                </div>
              )}
              <ContextMenuItem
                label="Duplicate"
                shortcut={`${modKey}D`}
                onClick={handleDuplicate}
              />
              {isConnected && (
                <ContextMenuItem
                  label="Disconnect"
                  onClick={handleDisconnect}
                />
              )}
              <div style={{ height: 1, background: "var(--mantine-color-default-border, #dee2e6)", margin: "2px 0" }} />
              <ContextMenuItem
                label="Delete"
                shortcut={isMac ? "\u232B" : "Del"}
                color="var(--mantine-color-red-6, #e03131)"
                hoverColor="var(--mantine-color-red-light-hover, #ffe3e3)"
                onClick={() => handleDelete()}
              />
            </>
          ) : (
            <>
              {/* Scene context menu (right-click on empty space) */}
              <ContextMenuItem
                label={ikEnabled ? "Disable IK Mode" : "Enable IK Mode"}
                onClick={() => {
                  const newEnabled = !ikEnabled;
                  setIKEnabled(newEnabled);
                  sendEnableIKMode(newEnabled);
                  setContextMenu(null);
                }}
              />
              {selectedInstance && (
                <>
                  <div style={{ height: 1, background: "var(--mantine-color-default-border, #dee2e6)", margin: "2px 0" }} />
                  <div style={{
                    padding: "4px 12px",
                    fontSize: 12,
                    color: "var(--mantine-color-dimmed, #868e96)",
                    borderBottom: "1px solid var(--mantine-color-default-border, #dee2e6)",
                    marginBottom: 2,
                  }}>
                    {selectedInstance.module_name}
                  </div>
                  <ContextMenuItem
                    label="Duplicate"
                    shortcut={`${modKey}D`}
                    onClick={() => {
                      const pos = selectedInstance.position;
                      sendAddModule(selectedInstance.module_name, [pos[0] + 0.1, pos[1] + 0.1, pos[2]]);
                      setContextMenu(null);
                    }}
                  />
                  {isSelectedConnected && (
                    <ContextMenuItem
                      label="Disconnect"
                      onClick={() => {
                        if (selectedInstance.parent_instance_id && selectedInstance.parent_link_name && selectedInstance.child_link_name) {
                          recordDisconnect(selectedInstance.instance_id, selectedInstance.child_link_name, selectedInstance.parent_instance_id, selectedInstance.parent_link_name);
                        }
                        sendDisconnectInstance(selectedInstance.instance_id);
                        setContextMenu(null);
                      }}
                    />
                  )}
                  <div style={{ height: 1, background: "var(--mantine-color-default-border, #dee2e6)", margin: "2px 0" }} />
                  <ContextMenuItem
                    label="Delete"
                    shortcut={isMac ? "\u232B" : "Del"}
                    color="var(--mantine-color-red-6, #e03131)"
                    hoverColor="var(--mantine-color-red-light-hover, #ffe3e3)"
                    onClick={() => handleDelete(selectedInstance.instance_id)}
                  />
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
